
import React, { useState } from 'react';
import { Bike, Wallet, BrainCircuit, CalendarDays, Trash2, Plus, Users, Gauge, Clock, X, ChevronDown, ChevronUp, Power, PowerOff, MapPin, AlertCircle } from 'lucide-react';
import { Driver, SystemConfig, LogisticsProfile, DriverSchedule } from '../../types';
import { SmartNumberInput } from '../SmartNumberInput';
import { AngleSelector } from '../AngleSelector';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { DECISION_POLICY_PROFILES, DEFAULT_DECISION_POLICY_PROFILE } from '../../constants';

interface Props {
  config: SystemConfig;
  setConfig: (config: SystemConfig) => void;
  drivers: Driver[];
  setDrivers: React.Dispatch<React.SetStateAction<Driver[]>>;
  updateConfigWithAutoCosts: (updates: any) => void;
}

export const SettingsTabFleet: React.FC<Props> = ({ config, setConfig, drivers, setDrivers, updateConfigWithAutoCosts }) => {
  const [newDriverName, setNewDriverName] = useState('');
  const [expandedDriverId, setExpandedDriverId] = useState<string | null>(null);
  const activeDecisionPolicyId = config.oracle?.decisionPolicyProfile ?? DEFAULT_DECISION_POLICY_PROFILE;
  const activeDecisionPolicy = DECISION_POLICY_PROFILES[activeDecisionPolicyId] ?? DECISION_POLICY_PROFILES[DEFAULT_DECISION_POLICY_PROFILE];

  const weekDays = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

  const updateFleetConfig = (key: keyof LogisticsProfile, value: number) => {
    setConfig({ ...config, fleetConfig: { ...config.fleetConfig, [key]: value } });
  };

  const updateDriver = (id: string, updates: Partial<Driver>) => {
    setDrivers(prev => prev.map(d => d.id === id ? { ...d, ...updates } : d));
  };

  const handleAddDriver = () => {
    if (!newDriverName.trim()) return;
    const newDriver: Driver = {
      id: 'dr_' + Math.random().toString(36).substr(2, 9),
      name: newDriverName,
      status: 'active',
      isAvailable: true,
      speedFactor: 1.0,
      maxRangeKm: 10,
      avgSpeedKmH: 25,
      schedule: { activeDays: [1, 2, 3, 4, 5, 6], shifts: [{ start: "18:00", end: "23:00" }] }
    };
    setDrivers(prev => [...prev, newDriver]);
    setNewDriverName('');
    toast.success('Motoboy adicionado!');
  };

  const handleRemoveDriver = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (window.confirm("Deseja REMOVER permanentemente?")) {
      setDrivers(prev => prev.filter(d => d.id !== id));
    }
  };

  const toggleDriverActive = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setDrivers(prev => prev.map(d => d.id === id ? { ...d, status: d.status === 'inactive' ? 'active' : 'inactive' } : d));
  };

  const toggleDay = (driverId: string, dayIdx: number) => {
    setDrivers(prev => prev.map(d => {
      if (d.id === driverId) {
        const currentDays = d.schedule?.activeDays || [];
        const newActiveDays = currentDays.includes(dayIdx)
          ? currentDays.filter(day => day !== dayIdx)
          : [...currentDays, dayIdx].sort((a, b) => a - b);
        return { ...d, schedule: { ...d.schedule!, activeDays: newActiveDays } };
      }
      return d;
    }));
  };

  const addShift = (driverId: string) => {
    setDrivers(prev => prev.map(d => {
      if (d.id === driverId) {
        const newShifts = [...(d.schedule?.shifts || []), { start: "11:00", end: "14:00" }];
        return { ...d, schedule: { ...d.schedule!, shifts: newShifts } };
      }
      return d;
    }));
  };

  const removeShift = (driverId: string, shiftIdx: number) => {
    setDrivers(prev => prev.map(d => {
      if (d.id === driverId) {
        const currentShifts = d.schedule?.shifts || [];
        if (currentShifts.length <= 1) {
          toast.error("Deve haver pelo menos um turno ativo.");
          return d;
        }
        const newShifts = currentShifts.filter((_, i) => i !== shiftIdx);
        return { ...d, schedule: { ...d.schedule!, shifts: newShifts } };
      }
      return d;
    }));
  };

  const updateShiftTime = (driverId: string, shiftIdx: number, field: 'start' | 'end', value: string) => {
    setDrivers(prev => prev.map(d => {
      if (d.id === driverId && d.schedule) {
        const newShifts = [...d.schedule.shifts];
        newShifts[shiftIdx] = { ...newShifts[shiftIdx], [field]: value };
        return { ...d, schedule: { ...d.schedule, shifts: newShifts } };
      }
      return d;
    }));
  };

  const LogisticsParamsInputs = ({ profile, onUpdate, label }: { profile: LogisticsProfile, onUpdate: (k: keyof LogisticsProfile, v: number) => void, label: string }) => (
    <div className="bg-card-bg border border-border-dim rounded-3xl p-6 shadow-xl relative overflow-hidden">
      <div className="absolute top-0 left-0 w-1.5 h-full bg-status-info/50"></div>
      <div className="flex items-center gap-3 mb-6 border-b border-border-dim pb-4">
        <div className="p-2.5 bg-status-info/10 text-status-info rounded-xl border border-status-info/20"><BrainCircuit className="w-5 h-5" /></div>
        <div><h4 className="text-base font-black text-text-primary uppercase tracking-widest">{label}</h4><p className="text-xs text-text-muted font-bold uppercase tracking-tighter">Define como o sistema agrupa e otimiza rotas para sua frota.</p></div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <AngleSelector
          value={profile.clusteringMaxAngleDegrees ?? 90}
          onChange={(val) => onUpdate('clusteringMaxAngleDegrees', val)}
          color="#6366f1" min={10} max={180}
          description="Define o ângulo máximo permitido entre pedidos para que possam ser agrupados. Um ângulo menor cria rotas mais diretas."
        />
        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SmartNumberInput
            label="Zig-Zag (Tempo)"
            value={profile.zigzagIgnoreTimeMinutes ?? 4}
            onChange={(v) => onUpdate('zigzagIgnoreTimeMinutes', v)}
            description="Ignorar ângulo em viagens rápidas (< min)."
          />
          <SmartNumberInput
            label="Zig-Zag (Distância)"
            value={profile.clusteringMinDistanceForAngleCheck ?? 1.2}
            onChange={(v) => onUpdate('clusteringMinDistanceForAngleCheck', v)}
            description="Raio físico para regra Zig-Zag (km)."
          />
          <SmartNumberInput
            label="Raio de Agrupamento"
            value={profile.clusteringMaxRadiusKm ?? 3}
            onChange={(v) => onUpdate('clusteringMaxRadiusKm', v)}
            description="Distância máxima entre pedidos para agrupamento (km)."
          />
          <SmartNumberInput
            label="Tempo Atendimento"
            value={profile.serviceTimeMinutes ?? 3}
            onChange={(v) => onUpdate('serviceTimeMinutes', v)}
            description="Tempo médio de atendimento no cliente (min)."
          />
          <SmartNumberInput
            label="Margem SLA"
            value={profile.slaToleranceMinutes ?? 10}
            onChange={(v) => onUpdate('slaToleranceMinutes', v)}
            description="Margem de tolerância para agrupamento (min)."
          />
          <SmartNumberInput
            label="Bumerangue: Margem de Segurança"
            value={profile.boomerangSafetyMarginMinutes ?? Math.max(2, Math.floor((profile.slaToleranceMinutes ?? 10) / 2))}
            onChange={(v) => onUpdate('boomerangSafetyMarginMinutes', v)}
            min={0}
            description="Tempo extra (em minutos) que o sistema exige para considerar o bumerangue seguro. Quanto maior, mais conservador (menos bumerangues)."
          />
          <SmartNumberInput
            label="Bumerangue: Economia Mínima"
            value={profile.boomerangMinIdleSavings ?? 0.5}
            onChange={(v) => onUpdate('boomerangMinIdleSavings', v)}
            step={0.1}
            min={0}
            isCurrency
            description="Economia mínima (em R$) para usar bumerangue quando não há risco de atraso. Quanto maior, mais seletivo."
          />
          <SmartNumberInput
            label="Limite por Rota"
            value={profile.maxOrdersPerRoute ?? 5}
            onChange={(v) => onUpdate('maxOrdersPerRoute', v)}
            description="Número máximo de pedidos por rota/viagem."
          />
        </div>
      </div>
    </div>
  );

  const isCostUnconfigured = (config.fleetConfig.dailyFixedCost || 0) === 0 && (config.fleetConfig.driverFixedFeePerOrder || 0) === 0;

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-300 pb-28">
      <div><h3 className="text-3xl font-black text-text-primary mb-1 uppercase tracking-tighter">Gestão de Frota Própria</h3><p className="text-sm text-text-muted font-medium">Controle de custos da equipe interna, escala de trabalho e lógica de otimização de rotas.</p></div>

      <section className="bg-card-bg border border-border-dim rounded-3xl p-4 shadow-sm">
        <h4 className="text-xs font-black text-text-primary uppercase tracking-widest mb-1">Política de Decisão Ativa</h4>
        <p className="text-sm font-black text-status-info uppercase">{activeDecisionPolicy.label}</p>
        <p className="text-xs text-text-muted font-medium mt-1">{activeDecisionPolicy.description}</p>
      </section>

      {isCostUnconfigured && (
        <div className="bg-status-ready/10 border border-status-ready/20 p-5 rounded-3xl flex gap-4 items-start animate-pulse">
          <AlertCircle className="w-6 h-6 text-status-ready shrink-0" />
          <div>
            <h4 className="text-xs font-black text-status-ready uppercase tracking-widest mb-1">Configuração Necessária</h4>
            <p className="text-xs text-text-primary font-bold leading-relaxed uppercase">Informe os custos da sua frota abaixo para que o Cérebro Logístico consiga calcular seu lucro líquido real.</p>
          </div>
        </div>
      )}

      <section className="bg-card-bg border border-border-dim rounded-3xl p-6 shadow-xl relative overflow-hidden">
        <div className="flex items-center gap-3 mb-6 border-b border-border-dim pb-4">
          <div className="p-2.5 bg-status-ready/10 text-status-ready rounded-xl border border-status-ready/20"><Wallet className="w-5 h-5" /></div>
          <div><h4 className="text-base font-black text-text-primary uppercase tracking-widest">Estrutura de Custo da Frota</h4><p className="text-xs text-text-muted font-bold uppercase tracking-tighter">Valores base para cálculo de lucro e otimização de custos.</p></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <SmartNumberInput
            label="Diária Fixa"
            value={config.fleetConfig.dailyFixedCost || 0}
            onChange={(v) => updateFleetConfig('dailyFixedCost', v)}
            step={5} isCurrency
            description="Custo fixo diário em R$ pago a cada entregador ativo."
          />
          <SmartNumberInput
            label="Taxa por Entrega"
            value={config.fleetConfig.driverFixedFeePerOrder || 0}
            onChange={(v) => updateFleetConfig('driverFixedFeePerOrder', v)}
            step={0.50} isCurrency
            description="Valor em R$ pago por cada ponto de entrega (drop)."
          />
          <SmartNumberInput
            label="Duração do Turno"
            value={config.fleetConfig.averageShiftHours || 0}
            onChange={(v) => updateFleetConfig('averageShiftHours', v)}
            step={1} min={1}
            description="Duração média em horas dos turnos da equipe."
          />
        </div>
      </section>

      <LogisticsParamsInputs label="Lógica de Otimização de Rotas [Frota Própria]" profile={config.fleetConfig} onUpdate={updateFleetConfig} />

      <section className="space-y-4">
        <div className="flex items-center justify-between px-2"><h3 className="text-sm font-black text-text-muted uppercase tracking-[0.2em] flex items-center gap-2"><Users className="w-4 h-4" /> Equipe de Entregadores ({drivers.length})</h3></div>
        <div className="flex gap-3 bg-card-bg p-2 rounded-[2rem] border border-border-dim shadow-inner">
          <input type="text" placeholder="Nome do novo motoboy..." value={newDriverName} onChange={(e) => setNewDriverName(e.target.value)} className="flex-1 bg-transparent border-none px-6 py-4 text-base font-bold text-text-primary outline-none placeholder-text-muted" />
          <button onClick={handleAddDriver} disabled={!newDriverName.trim()} className="bg-sal-cyber-teal text-badge-text-inv px-8 rounded-[1.5rem] font-black text-xs uppercase tracking-widest hover:brightness-110 transition-all flex items-center gap-2 shadow-lg"><Plus className="w-4 h-4" /> Cadastrar</button>
        </div>
        <div className="grid grid-cols-1 gap-4">
          {drivers.map(driver => (
            <div key={driver.id} className="flex flex-col mb-2">
              <div onClick={() => setExpandedDriverId(expandedDriverId === driver.id ? null : driver.id)} className={`bg-card-bg border rounded-3xl p-4 flex flex-col gap-3 cursor-pointer transition-all duration-300 ${expandedDriverId === driver.id ? 'border-sal-cyber-teal/50 mb-0 rounded-b-none' : 'border-border-dim hover:border-border-strong shadow-sm'} ${driver.status === 'inactive' ? 'opacity-50 grayscale-[0.5]' : ''}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black text-badge-text-inv transition-colors ${driver.status !== 'inactive' ? 'bg-sal-cyber-teal' : 'bg-border-strong'}`}>{driver.name.charAt(0)}</div>
                    <div><div className="flex items-center gap-2"><span className="block font-black text-text-primary text-base uppercase">{driver.name}</span></div><div className="flex items-center gap-3 mt-1 text-xs font-bold text-text-muted uppercase"><span className="flex items-center gap-1"><Gauge className="w-3 h-3" /> FATOR VELOC. <span className="text-text-primary">{(driver.speedFactor || 1.0).toFixed(2)}x</span></span><span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> RAIO MÁX. <span className="text-text-primary">{(driver.maxRangeKm || driver.maxDistance || 10)} KM</span></span><span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> VELOC. MÉDIA <span className="text-text-primary">{(driver.avgSpeedKmH || 25).toFixed(0)} KM/H</span></span></div></div>
                  </div>
                  <div className="flex items-center gap-2"><button onClick={(e) => toggleDriverActive(e, driver.id)} className={`p-3 rounded-2xl transition-all border ${driver.status !== 'inactive' ? 'bg-status-ready/10 text-status-ready border-status-ready/20' : 'bg-bg-main/50 text-text-muted border-transparent'}`}>{driver.status !== 'inactive' ? <Power className="w-5 h-5" /> : <PowerOff className="w-5 h-5" />}</button><ChevronDown className={`w-5 h-5 text-text-muted transition-transform ${expandedDriverId === driver.id ? 'rotate-180 text-sal-cyber-teal' : ''}`} /><button onClick={(e) => handleRemoveDriver(e, driver.id)} className="p-3 rounded-2xl text-text-muted hover:text-status-critical transition-colors"><Trash2 className="w-5 h-5" /></button></div>
                </div>
              </div>
              <AnimatePresence>{expandedDriverId === driver.id && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden bg-bg-main/30 border-x border-b border-border-dim rounded-b-3xl pt-6 p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div><h4 className="text-xs font-black text-sal-cyber-teal uppercase tracking-widest mb-4 flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5" /> Escala de Dias</h4><div className="flex justify-between gap-1 bg-card-bg p-2 rounded-2xl border border-border-dim shadow-sm">{weekDays.map((day, idx) => (<button key={idx} onClick={() => toggleDay(driver.id, idx)} className={`w-9 h-9 rounded-xl text-sm font-black transition-all ${driver.schedule?.activeDays.includes(idx) ? 'bg-sal-cyber-teal text-badge-text-inv shadow-md' : 'bg-bg-main/50 text-text-muted hover:bg-bg-main'}`}>{day}</button>))}</div></div>
                    <div>
                      <h4 className="text-xs font-black text-sal-cyber-teal uppercase tracking-widest mb-4 flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> Turnos</h4>
                      <div className="space-y-3">
                        {driver.schedule?.shifts.map((shift, sIdx) => (
                          <div key={sIdx} className="flex items-center gap-2 bg-card-bg p-2 rounded-2xl border border-border-dim shadow-sm">
                            <input type="time" value={shift.start} onChange={(e) => updateShiftTime(driver.id, sIdx, 'start', e.target.value)} className="flex-1 bg-input-bg border border-input-border rounded-xl px-2 py-2 text-sm font-mono font-bold text-text-primary outline-none" />
                            <span className="text-text-muted font-bold text-sm">até</span>
                            <input type="time" value={shift.end} onChange={(e) => updateShiftTime(driver.id, sIdx, 'end', e.target.value)} className="flex-1 bg-input-bg border border-input-border rounded-xl px-2 py-2 text-sm font-mono font-bold text-text-primary outline-none" />
                            {driver.schedule!.shifts.length > 1 && (
                              <button onClick={() => removeShift(driver.id, sIdx)} className="p-2 text-text-muted hover:text-status-critical transition-colors"><Trash2 className="w-4 h-4" /></button>
                            )}
                          </div>
                        ))}
                        <button onClick={() => addShift(driver.id)} className="w-full py-2.5 text-xs font-black text-sal-cyber-teal bg-sal-cyber-teal/5 hover:bg-sal-cyber-teal/10 rounded-xl transition-all uppercase tracking-widest border border-sal-cyber-teal/10">+ Adicionar Turno</button>
                      </div>
                    </div>
                  </div>
                  <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-card-bg border border-border-dim p-4 rounded-2xl shadow-sm">
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-black text-text-muted uppercase tracking-widest">Fator Velocidade</label>
                        <BrainCircuit className="w-3.5 h-3.5 text-sal-cyber-teal" />
                      </div>
                      <div className="text-xl font-black text-text-primary">{(driver.speedFactor ?? 1.0).toFixed(2)}x</div>
                      <p className="text-xs text-text-muted font-medium uppercase mt-1">Inteligência Sofia (Automático)</p>
                    </div>

                    <SmartNumberInput
                      label="Raio Máximo"
                      value={driver.maxRangeKm ?? driver.maxDistance ?? 10}
                      onChange={(v) => updateDriver(driver.id, { maxRangeKm: v })}
                      step={1} min={1} max={50}
                      description="Distância máxima em KM para entregas (limite de rota)."
                    />

                    <div className="bg-card-bg border border-border-dim p-4 rounded-2xl shadow-sm">
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-black text-text-muted uppercase tracking-widest">Velocidade Média</label>
                        <Gauge className="w-3.5 h-3.5 text-sal-cyber-teal" />
                      </div>
                      <div className="text-xl font-black text-text-primary">{(driver.avgSpeedKmH ?? 25).toFixed(0)} <span className="text-sm text-text-muted">KM/H</span></div>
                      <p className="text-xs text-text-muted font-medium uppercase mt-1">Estimativa Real em Rota</p>
                    </div>
                  </div>
                </motion.div>
              )}</AnimatePresence>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
